// SPDX-License-Identifier: AGPL-3.0-or-later

import {EMAIL_DESCRIPTOR, PASSWORD_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {shouldShowClaimedAccountSections} from '@app/features/user/components/settings_utils/section_registry/SectionRegistryHelpers';
import type {SectionDefinition} from '@app/features/user/components/settings_utils/section_registry/SectionRegistryTypes';
import {
	ACCOUNT_DESCRIPTOR,
	ADDRESS_DESCRIPTOR,
	AUTHENTICATION_DESCRIPTOR,
	AUTHENTICATOR_DESCRIPTOR,
	AUTHORIZED_APPS_DESCRIPTOR,
	BACKUP_CODES_DESCRIPTOR,
	BIOMETRIC_DESCRIPTOR,
	BIOMETRICS_DESCRIPTOR,
	BLOCKED_USERS_DESCRIPTOR,
	CHANGE_PASSWORD_DESCRIPTOR,
	CLOSE_ACCOUNT_DESCRIPTOR,
	CODES_DESCRIPTOR,
	CONTACT_DESCRIPTOR,
	CREDENTIALS_DESCRIPTOR,
	DELETE_DESCRIPTOR,
	FINGERPRINT_DESCRIPTOR,
	LINKED_DEVICES_DESCRIPTOR,
	LOGIN_DESCRIPTOR,
	MAIL_DESCRIPTOR,
	TWO_FA_DESCRIPTOR as MESSAGE_2FA_DESCRIPTOR,
	PASSKEY_DESCRIPTOR,
	PASSKEYS_DESCRIPTOR,
	PASSWORDLESS_DESCRIPTOR,
	RECOVERY_DESCRIPTOR,
	REMOVE_DESCRIPTOR,
	SECURITY_DESCRIPTOR as SECURITY_2_DESCRIPTOR,
	SECURITY_KEY_DESCRIPTOR,
	TWO_FACTOR_DESCRIPTOR,
} from '@app/features/user/components/settings_utils/section_registry/SharedDescriptors';
import {msg} from '@lingui/core/macro';

const MFA_DESCRIPTOR = msg({
	message: 'MFA',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const TOTP_DESCRIPTOR = msg({
	message: 'TOTP',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const OTP_DESCRIPTOR = msg({
	message: 'OTP',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const MESSAGE_2FA_BACKUP_DESCRIPTOR = msg({
	message: '2FA backup',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const WEBAUTHN_DESCRIPTOR = msg({
	message: 'WebAuthn',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const YUBIKEY_DESCRIPTOR = msg({
	message: 'YubiKey',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

const ACCOUNT_REMOVAL_DESCRIPTOR = msg({
	message: 'Account removal',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const PERMANENT_DELETION_DESCRIPTOR = msg({
	message: 'Permanent deletion',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const SIGN_IN_DETAILS_DESCRIPTOR = msg({
	message: 'Sign-in details',
	context: 'security-settings-section',
	comment: 'Account and security settings section for email address and password controls.',
});
const SECURITY_DESCRIPTOR = msg({
	message: 'Security',
	context: 'security-settings-section',
	comment: 'Account and security settings section for password, MFA, and sessions.',
});
const DANGER_ZONE_DESCRIPTOR = msg({
	message: 'Danger zone',
	context: 'security-settings-section',
	comment: 'Account and security settings section for destructive account actions.',
});

export const accountSecuritySections = [
	{
		id: 'account',
		tabType: 'account_security',
		label: SIGN_IN_DETAILS_DESCRIPTOR,
		keywords: [
			ACCOUNT_DESCRIPTOR,
			EMAIL_DESCRIPTOR,
			MAIL_DESCRIPTOR,
			ADDRESS_DESCRIPTOR,
			CONTACT_DESCRIPTOR,
			PASSWORD_DESCRIPTOR,
			CREDENTIALS_DESCRIPTOR,
			LOGIN_DESCRIPTOR,
			CHANGE_PASSWORD_DESCRIPTOR,
		],
		isAdvanced: false,
		isVisible: shouldShowClaimedAccountSections,
	},
	{
		id: 'security',
		tabType: 'account_security',
		label: SECURITY_DESCRIPTOR,
		keywords: [
			PASSWORD_DESCRIPTOR,
			CREDENTIALS_DESCRIPTOR,
			LOGIN_DESCRIPTOR,
			SECURITY_2_DESCRIPTOR,
			CHANGE_PASSWORD_DESCRIPTOR,
			MESSAGE_2FA_DESCRIPTOR,
			TWO_FACTOR_DESCRIPTOR,
			MFA_DESCRIPTOR,
			AUTHENTICATION_DESCRIPTOR,
			AUTHENTICATOR_DESCRIPTOR,
			TOTP_DESCRIPTOR,
			OTP_DESCRIPTOR,
			BACKUP_CODES_DESCRIPTOR,
			RECOVERY_DESCRIPTOR,
			CODES_DESCRIPTOR,
			MESSAGE_2FA_BACKUP_DESCRIPTOR,
			PASSKEY_DESCRIPTOR,
			PASSKEYS_DESCRIPTOR,
			WEBAUTHN_DESCRIPTOR,
			PASSWORDLESS_DESCRIPTOR,
			'Face ID',
			'Touch ID',
			BIOMETRIC_DESCRIPTOR,
			BIOMETRICS_DESCRIPTOR,
			FINGERPRINT_DESCRIPTOR,
			SECURITY_KEY_DESCRIPTOR,
			YUBIKEY_DESCRIPTOR,
			AUTHORIZED_APPS_DESCRIPTOR,
			LINKED_DEVICES_DESCRIPTOR,
		],
		isAdvanced: false,
		isVisible: shouldShowClaimedAccountSections,
	},
	{
		id: 'danger_zone',
		tabType: 'account_security',
		label: DANGER_ZONE_DESCRIPTOR,
		keywords: [
			DELETE_DESCRIPTOR,
			REMOVE_DESCRIPTOR,
			CLOSE_ACCOUNT_DESCRIPTOR,
			ACCOUNT_REMOVAL_DESCRIPTOR,
			PERMANENT_DELETION_DESCRIPTOR,
		],
		isAdvanced: false,
	},
	{
		id: 'blocked_users',
		tabType: 'account_security',
		label: BLOCKED_USERS_DESCRIPTOR,
		keywords: [BLOCKED_USERS_DESCRIPTOR],
		isAdvanced: false,
		isVisible: shouldShowClaimedAccountSections,
	},
] as const satisfies ReadonlyArray<SectionDefinition>;
